import { useEffect, useState } from "react";

const RESOLVE_MIN_MS = 500;
const PREVIEW_WAIT_MAX_MS = 1200;

interface SourceResolvingOptions {
    sourceKey: string;
    phase: "loading" | "resolved" | "unavailable";
    addressReady: boolean;
    discoverySettled: boolean;
}

interface RevealWindow {
    sourceKey: string;
    shownAt: number;
    resolvedAt: number | null;
    released: boolean;
}

/** Coordinate only the initial display. Verification and discovery retain their
 * real state, and later LP results/refreshes never hide a revealed balance. */
export function useSourceResolving({
    sourceKey,
    phase,
    addressReady,
    discoverySettled
}: SourceResolvingOptions): boolean {
    const [revealWindow, setWindow] = useState<RevealWindow | null>(null);

    useEffect(() => {
        if (phase === "unavailable") {
            setWindow(null);
            return;
        }
        if (revealWindow?.sourceKey !== sourceKey) {
            const now = Date.now();
            setWindow({ sourceKey, shownAt: now, resolvedAt: phase === "resolved" ? now : null, released: false });
            return;
        }
        if (revealWindow.released) return;
        if (phase === "resolved" && revealWindow.resolvedAt === null) {
            setWindow({ ...revealWindow, resolvedAt: Date.now() });
            return;
        }
        if (phase !== "resolved" || revealWindow.resolvedAt === null || !addressReady) return;

        // The cap begins after address classification. Slow Squads preflight or
        // an unsettled RPC read is actual loading, rather than an extra LP wait.
        const revealAt = Math.max(
            revealWindow.shownAt + RESOLVE_MIN_MS,
            discoverySettled ? 0 : revealWindow.resolvedAt + PREVIEW_WAIT_MAX_MS
        );
        const release = (): void => {
            setWindow((current) => (current?.sourceKey === sourceKey ? { ...current, released: true } : current));
        };
        const remaining = revealAt - Date.now();
        if (remaining <= 0) {
            release();
            return;
        }
        const timer = setTimeout(release, remaining);
        return (): void => clearTimeout(timer);
    }, [sourceKey, phase, addressReady, discoverySettled, revealWindow]);

    // A new source must suppress the previous preview on its first render,
    // before effects initialize its own timing window.
    return phase !== "unavailable" && (revealWindow?.sourceKey !== sourceKey || !revealWindow.released);
}
